import { ArrowLeft, Calendar, CheckCircle2, Loader2 } from 'lucide-react';
import { Loading } from '@/core/ui/components/Loading';
import { formatCurrency } from '@yawi/infrastructure';
import type { ReconciliationItem } from '@yawi/types';
import { useBankReconciliation } from '../hooks/useBankReconciliation';
import { ReportExportButton } from '../components';

interface BankReconciliationPageProps {
  onBack: () => void;
}

function formatDate(d: string): string {
  const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : d;
}

export const BankReconciliationPage = ({ onBack }: BankReconciliationPageProps) => {
  const {
    report,
    loading,
    exporting,
    reconcilingId,
    params,
    setMonth,
    setManualFields,
    toggleReconciled,
    exportReport,
  } = useBankReconciliation();

  const renderItems = (title: string, items: ReconciliationItem[], total: number, accent: 'green' | 'red') => (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <span className="font-semibold text-slate-800 text-sm">{title}</span>
        <span className={`font-semibold text-sm ${accent === 'green' ? 'text-[#639922]' : 'text-[#E24B4A]'}`}>
          {formatCurrency(total)}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="px-5 py-4 text-sm text-slate-400">Ninguno — todo conciliado</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {items.map((item) => (
            <div key={item.sourceId} className="flex items-center justify-between px-5 py-2.5 gap-3">
              <div className="min-w-0">
                <p className="text-sm text-slate-700 truncate">{item.concept}</p>
                <p className="text-xs text-slate-400">
                  {formatDate(item.date)}
                  {item.reference ? ` · ${item.reference}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-sm tabular-nums text-slate-700">{formatCurrency(item.amount)}</span>
                <button
                  onClick={() => toggleReconciled(item, true)}
                  disabled={reconcilingId === item.sourceId}
                  className="inline-flex items-center gap-1 text-xs font-medium text-[#639922] border border-[#639922]/40 rounded-full px-2.5 py-1 hover:bg-[#639922]/10 disabled:opacity-50"
                >
                  {reconcilingId === item.sourceId ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <CheckCircle2 size={12} />
                  )}
                  Conciliar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Conciliación Bancaria</h2>
            <p className="text-slate-500 text-sm">Saldo según libros vs. saldo según banco</p>
          </div>
        </div>
        <ReportExportButton onExport={exportReport} loading={exporting} disabled={loading || !report} />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
              <Calendar size={16} /> Mes
            </label>
            <input
              type="month"
              value={params.month}
              onChange={(e) => setMonth(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Saldo inicial</label>
            <input
              type="number"
              value={params.openingBalance ?? ''}
              onChange={(e) =>
                setManualFields({ openingBalance: e.target.value ? parseFloat(e.target.value) : undefined })
              }
              placeholder="0.00"
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm w-40"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Saldo según banco</label>
            <input
              type="number"
              value={params.bankBalance ?? ''}
              onChange={(e) =>
                setManualFields({ bankBalance: e.target.value ? parseFloat(e.target.value) : undefined })
              }
              placeholder="0.00"
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm w-40"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : !report ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">
          No hay datos para este periodo.
        </div>
      ) : (
        <div className="space-y-5">
          <p className="text-sm text-slate-500 capitalize">{report.monthLabel}</p>

          {/* Reconciliation summary */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="divide-y divide-slate-100">
              {[
                { label: 'Saldo según libros', value: report.bookBalance, strong: true },
                { label: 'Saldo según banco', value: report.bankBalance, strong: false },
                { label: '(+) Depósitos en tránsito', value: report.depositsInTransitTotal, color: '#639922' },
                { label: '(−) Cheques en circulación', value: report.outstandingChecksTotal, color: '#E24B4A' },
                { label: 'Saldo ajustado según banco', value: report.adjustedBankBalance, strong: true },
              ].map((row, i) => (
                <div key={i} className="flex items-center justify-between px-5 py-2.5">
                  <span className={`text-sm ${row.strong ? 'font-bold text-slate-800' : 'text-slate-600'}`}>
                    {row.label}
                  </span>
                  <span
                    className="text-sm tabular-nums font-medium"
                    style={{ color: row.color ?? (row.strong ? '#1e293b' : '#334155') }}
                  >
                    {formatCurrency(row.value)}
                  </span>
                </div>
              ))}
              <div
                className={`flex items-center justify-between px-5 py-3 ${
                  report.isReconciled ? 'bg-[#639922]/10' : 'bg-[#E24B4A]/10'
                }`}
              >
                <span className="text-sm font-bold text-slate-800">
                  Diferencia {report.isReconciled ? '· Conciliado ✓' : '· Pendiente'}
                </span>
                <span
                  className={`text-base font-bold tabular-nums ${
                    report.isReconciled ? 'text-[#639922]' : 'text-[#E24B4A]'
                  }`}
                >
                  {formatCurrency(report.difference)}
                </span>
              </div>
            </div>
          </div>

          {renderItems(
            'Depósitos en tránsito (ingresos no conciliados)',
            report.depositsInTransit,
            report.depositsInTransitTotal,
            'green',
          )}
          {renderItems(
            'Cheques en circulación (egresos no conciliados)',
            report.outstandingChecks,
            report.outstandingChecksTotal,
            'red',
          )}
        </div>
      )}
    </div>
  );
};
